'use client'

import { useEffect, useRef, useState } from 'react'
import {
  detectSocialProvider,
  aspectRatioFor,
  SOCIAL_SCRIPT,
  type EmbedOptions,
  type SocialProvider,
} from '@sifly/shared-utils'
import { loadEmbedScript } from './EmbedScriptLoader'
import { EmbedFallbackCard } from './EmbedFallbackCard'

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
}

const RENDER_TIMEOUT_MS = 5000

/**
 * IntersectionObserver-gated social embed. Renders the provider's
 * blockquote shape, lazy-loads the provider script when the section
 * scrolls into view, and falls back to a link card if the script fails
 * or doesn't render the embed within ~5s.
 */
export function SocialEmbedSection({
  url,
  options,
}: {
  url: string
  options?: EmbedOptions
}) {
  const detected = detectSocialProvider(url)
  const ref = useRef<HTMLDivElement>(null)
  const [scriptStatus, setScriptStatus] = useState<'idle' | 'loading' | 'rendered' | 'failed'>(
    'idle',
  )

  useEffect(() => {
    if (!detected) return
    const node = ref.current
    if (!node) return
    const obs = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            obs.disconnect()
            setScriptStatus('loading')
            loadEmbedScript(SOCIAL_SCRIPT[detected.provider])
              .then(() => {
                triggerRender(detected.provider)
                // Give the provider script a window to render the
                // blockquote into the actual embed; if it doesn't,
                // assume the post is gone or the script can't render.
                const timeout = setTimeout(() => {
                  if (!hasRendered(node, detected.provider)) {
                    setScriptStatus('failed')
                  } else {
                    setScriptStatus('rendered')
                  }
                }, RENDER_TIMEOUT_MS)
                return () => clearTimeout(timeout)
              })
              .catch(() => setScriptStatus('failed'))
            break
          }
        }
      },
      { rootMargin: '200px' },
    )
    obs.observe(node)
    return () => obs.disconnect()
  }, [detected])

  if (!detected) {
    return (
      <EmbedFallbackCard
        url={url}
        providerLabel="Social"
        reason="This URL doesn't look like Instagram, TikTok, or X."
      />
    )
  }

  if (scriptStatus === 'failed') {
    return (
      <EmbedFallbackCard
        url={url}
        providerLabel={PROVIDER_LABEL[detected.provider]}
      />
    )
  }

  const maxWidth = Number(options?.max_width ?? 550)
  const theme = (options?.theme === 'dark' ? 'dark' : 'light') as 'light' | 'dark'
  const aspect = aspectRatioFor(detected)

  return (
    <div className="not-prose my-6 mx-auto w-full" style={{ maxWidth: `${maxWidth}px` }}>
      <div
        ref={ref}
        className="w-full"
        style={{
          minHeight: scriptStatus === 'rendered' ? undefined : `${maxWidth * (aspectToRatio(aspect))}px`,
        }}
      >
        {detected.provider === 'instagram' && (
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={url}
            data-instgrm-version="14"
            data-instgrm-captioned
            style={{ background: '#fff', border: 0, margin: 0, padding: 0, width: '100%' }}
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              View on Instagram
            </a>
          </blockquote>
        )}
        {detected.provider === 'tiktok' && (
          <blockquote
            className="tiktok-embed"
            cite={url}
            data-video-id={detected.id}
            style={{ maxWidth: `${maxWidth}px`, minWidth: '280px', margin: 0 }}
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              View on TikTok
            </a>
          </blockquote>
        )}
        {detected.provider === 'x' && (
          <blockquote
            className="twitter-tweet"
            data-theme={theme}
            data-conversation={options?.hide_caption ? 'none' : undefined}
            data-dnt="true"
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              View on X
            </a>
          </blockquote>
        )}
      </div>
    </div>
  )
}

function aspectToRatio(aspect: string): number {
  const m = aspect.match(/^(\d+)\s*\/\s*(\d+)/)
  if (!m) return 0.5625
  return Number(m[2]) / Number(m[1])
}

function hasRendered(node: HTMLElement, provider: SocialProvider): boolean {
  if (provider === 'instagram') {
    return Boolean(node.querySelector('iframe.instagram-media-rendered, iframe[id^="instagram-embed-"]'))
  }
  if (provider === 'tiktok') {
    return Boolean(node.querySelector('iframe'))
  }
  if (provider === 'x') {
    return Boolean(node.querySelector('iframe[id^="twitter-widget"]'))
  }
  return false
}

function triggerRender(provider: SocialProvider) {
  if (typeof window === 'undefined') return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- third-party embed SDK globals (instgrm / twttr / FB) are not in the TS DOM lib
  const w = window as any
  if (provider === 'instagram') {
    if (w.instgrm?.Embeds?.process) w.instgrm.Embeds.process()
  } else if (provider === 'x') {
    if (w.twttr?.widgets?.load) w.twttr.widgets.load()
  }
  // tiktok's embed.js auto-scans on load; no manual trigger needed.
}
